import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "./AuthLayout";
import { Button, TextField, Callout } from "@/design/components";
import { resetPassword } from "@/api/auth";
import { ApiError } from "@/api/client";
import authStyles from "./AuthForm.module.css";

// Mirrors api resetPasswordSchema (password min 8) + a client-only confirm field.
const schema = z
  .object({
    password: z.string().min(8, "At least 8 characters"),
    confirm: z.string().min(1, "Re-enter your password"),
  })
  .refine((v) => v.password === v.confirm, { path: ["confirm"], error: "Passwords don't match" });
type Values = z.infer<typeof schema>;

/**
 * Set a new password from the emailed `/auth/reset-password?token=…` link
 * (consumes the one-time reset token), then sign in.
 */
export function ResetPasswordScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const doneRef = useRef<HTMLDivElement>(null);
  // Move focus to the confirmation when it appears so AT reliably announces it.
  useEffect(() => {
    if (done) doneRef.current?.focus();
  }, [done]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { password: "", confirm: "" } });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await resetPassword(token, values.password);
      setDone(true);
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : "We couldn't reset your password. The link may have expired or already been used.",
      );
    }
  });

  return (
    <AuthLayout>
      <header className={authStyles.head}>
        <p className="u-eyebrow">Account recovery</p>
        <h2 className={authStyles.title}>{done ? "Password updated" : "Choose a new password"}</h2>
      </header>

      {!token && (
        <Callout tone="danger" title="Missing token">
          This reset link is incomplete. Use the most recent link from your email, or request a new one.
        </Callout>
      )}

      {token && done && (
        <div ref={doneRef} tabIndex={-1} className={authStyles.focusResult}>
          <Callout tone="success" title="Your password is set">
            You can now sign in with your new password.
          </Callout>
          <Button size="lg" fullWidth onClick={() => navigate("/login", { replace: true })}>
            Continue to sign in
          </Button>
        </div>
      )}

      {token && !done && (
        <form className={authStyles.form} onSubmit={onSubmit} noValidate>
          {formError && (
            <div className={authStyles.formError} role="alert">
              {formError}
            </div>
          )}
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            required
            error={errors.password?.message}
            {...register("password")}
          />
          <TextField
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            placeholder="Re-enter your password"
            required
            error={errors.confirm?.message}
            {...register("confirm")}
          />
          <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
            Update password
          </Button>
        </form>
      )}

      {!done && (
        <p className={authStyles.alt}>
          <Link to="/forgot-password">Request a new link</Link> · <Link to="/login">Sign in</Link>
        </p>
      )}
    </AuthLayout>
  );
}
